import React, { useEffect, useRef, useState } from "react";

/**
 * Display type that rides up into place a word at a time.
 *
 * Each word gets its own clipping box, so the words do not fade in — they
 * arrive from underneath the line, which is the difference between a headline
 * that was designed and a div that was animated. The stagger is a per-word
 * transition-delay rather than a timer, so the whole reveal is one style pass
 * and the browser owns the timing.
 *
 * Two ways to trigger it:
 *  - pass `show` and drive it yourself, which is what the hero does so its
 *    entrance can be choreographed against the rest of the section;
 *  - pass nothing and it watches for itself, which is what every section
 *    further down the page does.
 *
 * Under reduced motion the mask rule in the stylesheet drops the transform
 * entirely, so the words are simply present.
 */

/** Words already split by line, or a plain string to break on spaces. */
function toLines(lines, text) {
  if (lines) return lines;
  if (!text) return [];
  return [text.split(/\s+/)];
}

function SplitText({
  lines,
  text,
  show,
  as: Tag = "span",
  className = "",
  lineClassName = "",
  delay = 0,
  stagger = 58
}) {
  const controlled = show !== undefined;
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  /* A controlled instance can mount with `show` already true — the leadership
     panel does exactly that, remounting via `key` each time the person
     changes. An element that mounts in its final state never transitions, so
     the words would simply appear. Holding the closed state for one painted
     frame gives the transition somewhere to travel from.

     Two frames, not one: a state change inside the first callback can still
     be committed before the browser has painted the closed state. */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!controlled) return;

    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setReady(true));
    });

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [controlled]);

  useEffect(() => {
    if (controlled) return;

    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setSeen(true);
        observer.disconnect();
      },
      { threshold: 0.15, rootMargin: "0px 0px -80px 0px" }
    );

    observer.observe(node);

    // Same failsafe reasoning as Reveal: the mask starts fully translated, so
    // an observer that never fires would hide the headline for good.
    const failsafe = setTimeout(() => setSeen(true), 1400);

    return () => {
      clearTimeout(failsafe);
      observer.disconnect();
    };
  }, [controlled]);

  const open = controlled ? show && ready : seen;
  const rows = toLines(lines, text);

  // Counted across lines rather than restarting each one, so the stagger
  // reads as a single sweep through the headline.
  let index = -1;

  return (
    <Tag ref={ref} className={className}>
      {rows.map((words, row) => (
        <span key={row} className={`block ${lineClassName}`}>
          {words.map((word, i) => {
            index += 1;
            return (
              <React.Fragment key={`${row}-${i}`}>
                <span className={`mw-mask ${open ? "mw-mask-in" : ""}`}>
                  <span style={{ transitionDelay: `${delay + index * stagger}ms` }}>
                    {word}
                  </span>
                </span>
                {i < words.length - 1 ? " " : null}
              </React.Fragment>
            );
          })}
        </span>
      ))}
    </Tag>
  );
}

export default SplitText;
